import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import {
  CheckCircle,
  Plus,
  Minus,
  Mail,
  MapPin,
  ShieldCheck,
  BadgeCheck,
  MessageCircle,
  PlusSquare,
} from "lucide-react";

import img99 from "../../../assets/img99.jpeg";
import img7 from "../../../assets/img7.webp";
import img8 from "../../../assets/img8.webp";
import img9 from "../../../assets/img9.webp";
import img10 from "../../../assets/img10.jpg";
import img66 from "../../../assets/img66.jpg";
import img67 from "../../../assets/img67.jpg";
import img69 from "../../../assets/img69.jpg";
import partner3 from "../../../assets/partners/img3.webp";
import partner8 from "../../../assets/partners/img8.webp";
import partner9 from "../../../assets/partners/img9.webp";
import partner10 from "../../../assets/partners/img10.webp";
import partner11 from "../../../assets/partners/img11.webp";
import partner15 from "../../../assets/partners/img15.webp";
import Testimonials from "../../../components/Testimonials";

function ThirtyMinuteTimerCard({ stepsInView, makeFadeScale }) {
  const DURATION_SECONDS = 1800; // 30 minutes
  const remainingRef = React.useRef(DURATION_SECONDS);
  const [remaining, setRemaining] = React.useState(DURATION_SECONDS);

  React.useEffect(() => {
    setRemaining(remainingRef.current);
    const id = setInterval(() => {
      if (remainingRef.current > 0) {
        remainingRef.current -= 1;
        setRemaining(remainingRef.current);
      } else {
        clearInterval(id);
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  const pad2 = (n) => String(n).padStart(2, "0");

  return (
    <div
      style={makeFadeScale(stepsInView, 240, 0.988, 780)}
      className="bg-white rounded-md shadow-[0_12px_30px_rgba(0,0,0,0.18)] border border-gray-200 px-4 py-6 sm:px-6 sm:py-8 max-w-[640px]"
    >
      <h3 className="text-center font-bold text-[#49628b] text-lg sm:text-2xl">
        Temps restant pour votre rencontre GRATUITE
      </h3>

      <div className="mt-6 flex justify-center items-start gap-4 sm:gap-14 font-['Segoe_UI',Roboto,Helvetica,Arial,sans-serif]">
        <div className="text-center flex-1 sm:flex-none">
          <div className="flex items-center justify-center leading-none tabular-nums">
            <span className="text-[32px] sm:text-[60px] font-bold text-[#20a7df]">
              {hours}
            </span>
            <span className="text-[28px] sm:text-[60px] font-bold text-[#20a7df] ml-1">
              :
            </span>
          </div>
          <div className="mt-1 text-[12px] sm:text-[16px] text-[#333] font-medium">
            Heures
          </div>
        </div>

        <div className="text-center flex-1 sm:flex-none">
          <div className="flex items-center justify-center leading-none tabular-nums">
            <span className="text-[32px] sm:text-[60px] font-bold text-[#20a7df]">
              {pad2(minutes)}
            </span>
            <span className="text-[28px] sm:text-[60px] font-bold text-[#2b8ece] ml-1">
              :
            </span>
          </div>
          <div className="mt-1 text-[12px] sm:text-[16px] text-[#333] font-medium">
            Minutes
          </div>
        </div>

        <div className="text-center flex-1 sm:flex-none">
          <div className="flex items-center justify-center leading-none tabular-nums">
            <span className="text-[32px] sm:text-[60px] font-bold text-[#2b8ece]">
              {pad2(seconds)}
            </span>
          </div>
          <div className="mt-1 text-[12px] sm:text-[16px] text-[#333] font-medium">
            Secondes
          </div>
        </div>
      </div>

      {remaining === 0 ? (
        <div className="mt-5 text-center text-red-500 font-semibold opacity-90">
          Minuteur terminé
        </div>
      ) : null}
    </div>
  );
}

const LifeInsuranceToronto = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [flippedStep, setFlippedStep] = useState(null);

  const formRef = useRef(null);
  const BACKEND_BASE =
    import.meta.env.VITE_BACKEND_URL || "https://backend.einsured.ca";

  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    coverageAmount: "",
    preferredContactTime: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    phone: false,
    email: false,
    coverageAmount: false,
    preferredContactTime: false,
  });

  const MAX_NAME_LEN = 32;

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const toggleStepFlip = (idx) => {
    setFlippedStep((cur) => (cur === idx ? null : idx));
  };

  const siteContainer = "mx-auto w-full max-w-[1380px]";

  const useInViewOnce = (threshold = 0.22) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        },
        { threshold }
      );

      io.observe(el);
      return () => io.disconnect();
    }, [threshold]);

    return [ref, inView];
  };

  const ease = "cubic-bezier(0.22, 1, 0.36, 1)";
  const ENTRANCE_DUR_MULT = 1.55;
  const ENTRANCE_DIST_MULT = 1.9;

  const dur = (ms) => Math.round(ms * ENTRANCE_DUR_MULT);
  const dist = (px) => Math.round(px * ENTRANCE_DIST_MULT);

  const scaleFromBoost = (scaleFrom) => {
    const delta = 1 - Number(scaleFrom || 1);
    const boosted = 1 - delta * ENTRANCE_DIST_MULT;
    return Math.max(0.94, boosted);
  };

  const makeFadeUp = (inView, delayMs = 0, y = 16, durMs = 700) => {
    const Y = dist(y);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0px)" : `translateY(${Y}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeScale = (
    inView,
    delayMs = 0,
    scaleFrom = 0.985,
    durMs = 750
  ) => {
    const D = dur(durMs);
    const S = scaleFromBoost(scaleFrom);
    const Y = dist(10);

    return {
      opacity: inView ? 1 : 0,
      transform: inView
        ? "translateY(0px) scale(1)"
        : `translateY(${Y}px) scale(${S})`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeLeft = (inView, delayMs = 0, x = 18, durMs = 700) => {
    const X = dist(x);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateX(0px)" : `translateX(-${X}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeRight = (inView, delayMs = 0, x = 18, durMs = 700) => {
    const X = dist(x);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateX(0px)" : `translateX(${X}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const [stepsRef, stepsInView] = useInViewOnce(0.18);

  const normalizeCanadianPhone = (phone) => {
    let digits = String(phone || "").replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return "";
    return digits;
  };

  const formatPhoneForDisplay = (digits10) => {
    if (!digits10 || digits10.length !== 10) return "";
    return `+1 (${digits10.slice(0, 3)}) ${digits10.slice(3, 6)}-${digits10.slice(6)}`;
  };

  const isEmailValid = (email) => {
    const v = String(email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v);
  };

  const isCanadianPhoneValid = (phone) => !!normalizeCanadianPhone(phone);

  const errors = useMemo(() => {
    const e = {};

    const nm = String(formData.name || "").trim();
    if (!nm) {
      e.name = "Veuillez entrer votre nom.";
    } else if (nm.length > MAX_NAME_LEN) {
      e.name = `Le nom doit comporter ${MAX_NAME_LEN} caractères ou moins.`;
    }

    const ph = String(formData.phone || "").trim();
    if (!ph) {
      e.phone = "Veuillez entrer votre numéro de téléphone.";
    } else if (!isCanadianPhoneValid(ph)) {
      e.phone = "Veuillez entrer un numéro de téléphone canadien valide.";
    }

    const em = String(formData.email || "").trim();
    if (!em) {
      e.email = "Veuillez entrer votre adresse courriel.";
    } else if (!isEmailValid(em)) {
      e.email = "Veuillez entrer une adresse courriel valide.";
    }

    return e;
  }, [formData.name, formData.phone, formData.email]);

  const hasAnyError = Object.keys(errors).length > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;

    if (name === "name") {
      next = String(value || "").slice(0, MAX_NAME_LEN);
    }

    if (name === "phone") {
      next = String(value || "").replace(/[^0-9+\-\s().]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: next }));

    if (submitted) setSubmitted(false);
    if (status) setStatus(null);
    if (errorMsg) setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = formRef.current;
    if (!form) return;

    setTouched({
      name: true,
      phone: true,
      email: true,
      coverageAmount: true,
      preferredContactTime: true,
    });

    if (hasAnyError) {
      setStatus("error");
      setErrorMsg(
        "Un ou plusieurs champs contiennent une erreur. Veuillez vérifier et réessayer."
      );
      setSubmitted(false);
      return;
    }

    try {
      setStatus("loading");
      setErrorMsg("");
      setSubmitted(false);

      const fd = new FormData();
      fd.append("your_name", (formData.name || "").trim());
      fd.append("your-phone", (formData.phone || "").trim());
      fd.append("your_email", (formData.email || "").trim());
      fd.append(
        "your-message",
        `Coverage Amount: ${(formData.coverageAmount || "").trim()}\nPreferred Contact Time: ${(formData.preferredContactTime || "").trim()}\nPage: Life Insurance in Toronto`
      );

      const res = await fetch(`${BACKEND_BASE}/api/addContactLead`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });

      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch {
        //
      }

      if (!res.ok) {
        throw new Error(
          json?.message || text || `Request failed (${res.status})`
        );
      }

      setStatus("success");
      setSubmitted(true);

      setFormData({
        name: "",
        phone: "",
        email: "",
        coverageAmount: "",
        preferredContactTime: "",
      });

      setTouched({
        name: false,
        phone: false,
        email: false,
        coverageAmount: false,
        preferredContactTime: false,
      });
    } catch (error) {
      setStatus("error");
      setErrorMsg(error?.message || "Une erreur est survenue. Veuillez réessayer.");
      setSubmitted(false);
    }
  };

  const advisorCards = [
    {
      name: "Aman Ahuja",
      image: img66,
      profileLink: "/our-team/aman",
      role: "PDG et courtier d'assurance principal",
      license: "Conseiller en assurance vie agréé – Ontario",
      licenseNo: "Numéro de licence : XXXXX",
      experience: "14+ ans d'expérience",
      specialty: "Planification pour clients fortunés, assurance temporaire, protection des entreprises",
      bio: "Aide les familles, professionnels et propriétaires d'entreprise de Toronto à comparer les options d'assurance vie avec des conseils clairs et des recommandations personnalisées.",
      phone1: "tel:+14168166100",
      phone1Label: "(416) 816-6100",
      phone2: "tel:+14168400128",
      phone2Label: "(416) 840-0128 ext. 100",
      email: "mailto:aman@einsured.ca",
      emailLabel: "aman@einsured.ca",
    },
    {
      name: "Sunaina Ahuja",
      image: img67,
      profileLink: "/our-team/sunaina",
      role: "Directrice des opérations et courtière d'assurance",
      license: "Conseillère en assurance vie agréée – Ontario",
      licenseNo: "Numéro de licence : XXXXX",
      experience: "7+ ans d'expérience",
      specialty: "Assurance vie entière, planification successorale, protection familiale",
      bio: "Axée sur la planification de protection à long terme pour les familles, propriétaires et professionnels de Toronto qui recherchent une sécurité financière fiable.",
      phone1: "tel:+14165649000",
      phone1Label: "(416) 564-9000",
      phone2: "tel:+14168400128",
      phone2Label: "(416) 840-0128 ext. 101",
      email: "mailto:sunaina@einsured.ca",
      emailLabel: "sunaina@einsured.ca",
    },
    {
      name: "Shikha Ahuja",
      image: img69,
      profileLink: "/our-team/shikha",
      role: "Courtière d'assurance",
      license: "Conseillère en assurance vie agréée – Ontario",
      licenseNo: "Numéro de licence : XXXXX",
      experience: "6+ ans d'expérience",
      specialty: "Assurance vie universelle, couverture pour entrepreneurs, conseils personnalisés",
      bio: "Accompagne les clients de Toronto avec des stratégies d'assurance adaptées aux besoins des entreprises, à la protection hypothécaire et à la planification flexible à long terme.",
      phone1: "tel:+16475322101",
      phone1Label: "(647) 532-2101",
      phone2: "tel:+14168400128",
      phone2Label: "(416) 840-0128 ext. 102",
      email: "mailto:shikha@einsured.ca",
      emailLabel: "shikha@einsured.ca",
    },
  ];

  const insuranceOptions = [
    {
      title: "Assurance vie temporaire",
      desc: "Couverture abordable pour une période déterminée afin de protéger le revenu, les dépenses familiales et les obligations hypothécaires à Toronto.",
      bestFor: "Idéal pour : remplacement de revenu, protection hypothécaire",
      link: "/term-life-insurance",
      img: img7,
    },
    {
      title: "Assurance vie entière",
      desc: "Couverture permanente conçue pour la stabilité à long terme, la protection garantie et les objectifs de planification successorale.",
      bestFor: "Idéal pour : sécurité financière à long terme",
      link: "/whole-life-insurance",
      img: img8,
    },
    {
      title: "Assurance vie universelle",
      desc: "Assurance permanente flexible qui combine une protection viagère avec un potentiel d'épargne de type investissement.",
      bestFor: "Idéal pour : flexibilité investissement et protection",
      link: "/universal-life-insurance",
      img: img9,
    },
    {
      title: "Assurance vie sans examen médical",
      desc: "Options de couverture simplifiées pour les personnes qui souhaitent une approbation plus rapide sans exigences médicales traditionnelles.",
      bestFor: "Idéal pour : approbation rapide",
      link: "/no-medical-insurance",
      img: img10,
    },
    {
      title: "Assurance vie pour propriétaires d'entreprise",
      desc: "Solutions de couverture pour les entrepreneurs de Toronto, notamment la protection des personnes clés et le soutien à la planification de la relève.",
      bestFor: "Idéal pour : protection des personnes clés et planification de la relève",
      link: "/life-insurance",
      img: img7,
    },
  ];

  const faqs = [
    {
      question: "De quelle couverture d'assurance vie ai-je besoin à Toronto ?",
      answer:
        "Le bon montant dépend de votre revenu, vos dettes, votre solde hypothécaire, vos responsabilités familiales et vos objectifs futurs. De nombreux résidents de Toronto choisissent une couverture qui peut aider à remplacer le revenu et à se protéger contre le coût de vie élevé.",
    },
    {
      question: "Qu'est-ce qui affecte ma prime en Ontario ?",
      answer:
        "Les primes sont généralement basées sur des facteurs tels que l'âge, la santé, le tabagisme, le montant de la couverture, le type de police et les exigences de souscription. Votre profession et votre mode de vie peuvent également affecter le prix dans certains cas.",
    },
    {
      question: "Un examen médical est-il requis ?",
      answer:
        "Pas nécessairement. Certaines polices nécessitent une souscription médicale, tandis que d'autres offrent une approbation simplifiée ou sans examen médical selon votre âge, vos antécédents médicaux et le montant de la couverture demandé.",
    },
    {
      question: "Les travailleurs autonomes peuvent-ils faire une demande ?",
      answer:
        "Oui. Les travailleurs autonomes, consultants et propriétaires d'entreprise à Toronto peuvent faire une demande d'assurance vie et peuvent également explorer des options qui soutiennent la continuité des affaires ou la protection des personnes clés.",
    },
    {
      question: "Combien de temps prend l'approbation ?",
      answer:
        "Les délais d'approbation varient selon l'assureur et le type de police. Certaines options simplifiées peuvent être plus rapides, tandis que les polices entièrement souscrites peuvent prendre plus de temps si des preuves médicales sont requises.",
    },
    {
      question: "L'assurance vie est-elle imposable au Canada ?",
      answer:
        "Dans de nombreux cas, les prestations de décès d'assurance vie versées à un bénéficiaire nommé sont généralement reçues sans impôt au Canada, bien que le traitement fiscal puisse varier selon la structure de la police et la situation.",
    },
  ];

  const providerLogos = [
    { src: partner9, alt: "Logo Sun Life" },
    { src: partner3, alt: "Logo Manuvie" },
    { src: partner8, alt: "Logo Empire Vie" },
    { src: partner11, alt: "Logo Canada Vie" },
    { src: partner15, alt: "Logo Alliance industrielle" },
    { src: partner10, alt: "Logo Equitable Life" },
  ];

  return (
    <div style={{ fontFamily: '"Montserrat", sans-serif' }}>
      <SEO
        title="Assurance vie à Toronto, Ontario | Agents agréés – einsured.ca"
        description="Obtenez une assurance vie à Toronto, Ontario avec des conseillers agréés. Comparez les options temporaires, permanentes et sans examen médical pour vous."
        keywords="Assurance vie à Toronto, assurance vie Toronto, assurance vie temporaire Toronto, assurance vie entière Toronto, conseillers en assurance vie agréés Ontario"
      />

      {/* Hero */}
      <section
        className="relative min-h-[72vh] sm:min-h-[78vh] md:min-h-[85vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 lg:px-10"
        style={{ backgroundImage: `url(${img99})` }}
        role="img"
        aria-label="Bannière principale assurance vie à Toronto Ontario"
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div
          className={`relative z-10 ${siteContainer} px-0 sm:px-2 md:px-8 lg:px-12 text-white`}
        >
          <div className="max-w-3xl mx-auto md:mx-0 text-center md:text-left">
            <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center md:justify-start gap-3 mb-6">
              <span className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-xs sm:text-sm md:text-base backdrop-blur-sm">
                <BadgeCheck className="w-4 h-4 text-sky-300 shrink-0" />
                Courtiers agréés Ontario
              </span>
              <span className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-xs sm:text-sm md:text-base backdrop-blur-sm">
                <MapPin className="w-4 h-4 text-sky-300 shrink-0" />
                Au service de la région du Grand Toronto
              </span>
              <span className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-xs sm:text-sm md:text-base backdrop-blur-sm">
                <ShieldCheck className="w-4 h-4 text-sky-300 shrink-0" />
                Consultation confidentielle
              </span>
            </div>

            <h1 className="text-3xl leading-tight sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Assurance vie à Toronto, Ontario
            </h1>

            <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-8 font-medium max-w-2xl mx-auto md:mx-0 leading-relaxed">
              Des solutions d'assurance vie personnalisées pour les familles, professionnels et propriétaires d'entreprise de Toronto.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-center md:justify-start">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 border-2 border-sky-400 bg-sky-400 text-white rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-sky-600 hover:border-sky-600 transition-all duration-300 text-center"
              >
                Obtenir une soumission gratuite
              </a>

              <a
                href="https://www.einsured.ca/contact-us"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 border-2 border-white bg-transparent text-white rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-white hover:text-[#49628b] transition-all duration-300 text-center"
              >
                Parler à un conseiller agréé
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Local Introduction */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-5 sm:mb-6 leading-tight">
              Conseillers en assurance vie de confiance à Toronto
            </h2>
            <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700 mb-4 sm:mb-5">
              Nous aidons les familles, professionnels, entrepreneurs et propriétaires de Toronto à trouver des solutions d'assurance vie qui correspondent à leurs objectifs, responsabilités et budget. Que vous soyez axé sur la protection hypothécaire, le remplacement de revenu, la planification successorale ou la continuité des affaires, nos conseillers agréés prennent le temps de comprendre ce qui compte le plus pour vous.
            </p>
            <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700">
              Avec une solide compréhension des priorités financières de la RGT, de la hausse du coût de la vie et des besoins de protection des ménages urbains, nous facilitons la comparaison des polices, la compréhension des primes et votre démarche en toute confiance.
            </p>
          </div>
        </div>
      </section>

      {/* Experience / EEAT */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-gray-50 text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
              Notre expérience au service des résidents de Toronto
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700">
              Nous nous concentrons sur des conseils pratiques, des comparaisons transparentes et une planification personnalisée pour les clients de tout Toronto et de la RGT.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {[
              "Des années au service des clients ontariens avec une planification de protection personnalisée",
              "Expérience dans le soutien aux familles, professionnels et propriétaires d'entreprise de Toronto",
              "Conseils pour les clients gérant des coûts de vie élevés et des besoins de protection hypothécaire",
              "Consultations multilingues disponibles le cas échéant",
              "Évaluations de couverture personnalisées basées sur vos objectifs et votre budget",
              "Recommandations de conseillers agréés basées sur l'admissibilité et les facteurs de souscription",
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl shadow-md p-5 sm:p-6 flex items-start gap-3"
              >
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-sky-500 mt-0.5 shrink-0" />
                <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                  {item}
                </p>
              </div>
            ))}
          </div>

          <p className="max-w-5xl mx-auto mt-8 sm:mt-10 text-center text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
            Tous les conseils sont fournis par des conseillers en assurance vie agréés en Ontario, et les recommandations sont basées sur vos besoins personnels, vos priorités financières et les considérations de souscription.
          </p>
        </div>
      </section>

      {/* Advisors */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#49628b] mb-4 leading-tight">
              Rencontrez nos professionnels agréés en assurance vie
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700">
              Travaillez avec des conseillers agréés de l'Ontario qui se concentrent sur la protection, la planification et les recommandations personnalisées.
            </p>
          </div>

          <div className="grid gap-6 sm:gap-8 lg:gap-10 md:grid-cols-2 xl:grid-cols-3">
            {advisorCards.map((advisor, index) => (
              <div
                key={index}
                className="bg-[#12a5dd] rounded-[28px] sm:rounded-[32px] shadow-[0_18px_35px_rgba(0,0,0,0.12)] px-5 sm:px-7 py-7 sm:py-8 flex flex-col gap-5"
              >
                <div className="flex justify-center">
                  <img
                    src={advisor.image}
                    loading="lazy"
                    alt={advisor.name}
                    className="w-32 h-40 sm:w-40 sm:h-48 md:w-44 md:h-52 rounded-[22px] object-cover"
                  />
                </div>

                <div className="text-white">
                  <div className="flex justify-center mb-5">
                    <Link
                      to={advisor.profileLink}
                      className="bg-[#3b4f80] px-5 sm:px-8 py-2 rounded-full shadow-[0_10px_25px_rgba(0,0,0,0.25)] text-sm sm:text-base md:text-lg font-semibold text-center hover:bg-[#314069] transition-colors"
                    >
                      {advisor.name}
                    </Link>
                  </div>

                  <p className="text-sm sm:text-base mb-2 text-center leading-relaxed">
                    {advisor.role}
                  </p>

                  <p className="text-sm sm:text-base font-semibold mb-1 text-center text-white/95 leading-relaxed">
                    {advisor.license}
                  </p>

                  <p className="text-sm sm:text-base mb-2 text-center text-white/90">
                    {advisor.licenseNo}
                  </p>

                  <p className="text-sm sm:text-base mb-3 text-center text-white/90">
                    {advisor.experience}
                  </p>

                  <p className="text-sm sm:text-base mb-4 text-center text-white/95 leading-relaxed">
                    <span className="font-semibold">Spécialité :</span>{" "}
                    {advisor.specialty}
                  </p>

                  <p className="text-sm sm:text-base leading-relaxed text-center text-white/95 mb-6">
                    {advisor.bio}
                  </p>

                  <div className="space-y-3 w-full">
                    <a
                      href={advisor.phone1}
                      className="block bg-white text-[#49628b] text-sm sm:text-base font-medium rounded-xl px-4 sm:px-5 py-3 text-center w-full hover:bg-[#eef6ff] transition"
                    >
                      {advisor.phone1Label}
                    </a>

                    <a
                      href={advisor.phone2}
                      className="block bg-white text-[#49628b] text-sm sm:text-base font-medium rounded-xl px-4 sm:px-5 py-3 text-center w-full hover:bg-[#eef6ff] transition"
                    >
                      {advisor.phone2Label}
                    </a>

                    <a
                      href={advisor.email}
                      className="flex items-center justify-center gap-2 bg-white text-[#49628b] text-sm sm:text-base font-medium rounded-xl px-4 sm:px-5 py-3 break-all w-full hover:bg-[#eef6ff] transition"
                    >
                      <Mail className="w-4 h-4 shrink-0" />
                      <span>{advisor.emailLabel}</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#f8fbff] text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
              Options d'assurance vie à Toronto
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700">
              Explorez les types de couverture conçus pour les familles, professionnels et propriétaires d'entreprise de Toronto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
            {insuranceOptions.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition"
              >
                <img
                  loading="lazy"
                  src={item.img}
                  alt={item.title}
                  className="w-full h-48 sm:h-56 object-cover"
                />
                <div className="p-5 sm:p-7">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                    {item.desc}
                  </p>
                  <p className="text-sm sm:text-base md:text-lg text-sky-600 font-semibold mb-5 leading-relaxed">
                    {item.bestFor}
                  </p>
                  <Link
                    to={item.link}
                    className="inline-flex items-center justify-center border-2 border-sky-500 text-sky-600 px-5 sm:px-6 py-2.5 rounded-full font-semibold hover:bg-sky-500 hover:text-white transition w-full sm:w-auto"
                  >
                    En savoir plus
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section
        className="relative py-14 sm:py-20 md:py-28 px-4 sm:px-6 lg:px-10 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
        role="img"
        aria-label="Comment obtenir une assurance vie à Toronto"
      >
        <div className="absolute inset-0 bg-sky-900/75"></div>

        <div className={`relative z-10 ${siteContainer} text-white`}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Comment obtenir une assurance vie à Toronto
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-[#d5ebff]">
              Un processus simple avec des explications claires et un soutien de conseillers agréés à chaque étape.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 sm:gap-6">
            {[
              "Consultation gratuite des besoins",
              "Comparer les meilleurs assureurs canadiens",
              "Demande et souscription",
              "Approbation de la police",
              "Soutien continu à la police",
            ].map((step, index) => (
              <div
                key={index}
                className="p-5 sm:p-6 rounded-2xl shadow-lg bg-white/10 backdrop-blur-md border border-white/10 text-center"
              >
                <div className="w-11 h-11 sm:w-12 sm:h-12 mx-auto mb-4 rounded-full bg-sky-400 text-white flex items-center justify-center text-base sm:text-lg font-bold">
                  {index + 1}
                </div>
                <h3 className="text-base sm:text-lg xl:text-xl font-semibold text-white leading-snug">
                  {step}
                </h3>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto mt-8 sm:mt-10 flex flex-col md:flex-row items-center justify-center gap-3 sm:gap-4 text-center">
            <span className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-xs sm:text-sm md:text-base">
              <CheckCircle className="w-4 h-4 text-sky-300 shrink-0" />
              Soumission sans engagement
            </span>
            <span className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 px-4 py-2 rounded-full text-xs sm:text-sm md:text-base">
              <CheckCircle className="w-4 h-4 text-sky-300 shrink-0" />
              Explication transparente des prix
            </span>
          </div>
        </div>
      </section>

      {/* Providers */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
              Nous collaborons avec les principaux assureurs canadiens
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700">
              Nous comparons plusieurs assureurs pour trouver des options compétitives basées sur vos besoins et objectifs.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
            {providerLogos.map((provider, index) => (
              <div
                key={index}
                className="h-20 sm:h-24 md:h-28 rounded-2xl border border-gray-200 bg-[#f8fbff] flex items-center justify-center px-3 sm:px-4 shadow-sm"
              >
                <img
                  src={provider.src}
                  alt={provider.alt}
                  className="max-h-10 sm:max-h-12 md:max-h-14 w-auto object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white">
        <Testimonials limit={12} autoPlay={true} intervalMs={4500} />
      </section>

      {/* Service Area + Map */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#e8eff8] text-gray-800 relative border-t-4 border-[#1e48a0]">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
              Au service de toutes les zones de Toronto
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700">
              Nous accompagnons les clients de tout Toronto et de la RGT avec des options de consultation flexibles et des conseils de couverture personnalisés.
            </p>
          </div>

          <div className="max-w-[1380px] mx-auto bg-white rounded-3xl shadow-lg border border-gray-200 px-4 sm:px-8 lg:px-10 py-6 sm:py-10">
            <div className="flex flex-col md:flex-row gap-8 lg:gap-14 items-stretch">
              <div className="md:w-1/2 space-y-4 sm:space-y-5">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#49628b]">
                    Téléphone
                  </h3>
                  <a
                    href="tel:18444400128"
                    className="text-sm sm:text-base md:text-lg text-gray-800 hover:text-sky-500 transition"
                  >
                    (844) 440-0128
                  </a>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#49628b]">
                    Courriel
                  </h3>
                  <a
                    href="mailto:info@einsured.ca"
                    className="text-sm sm:text-base md:text-lg text-gray-800 hover:text-sky-500 break-all transition"
                  >
                    info@einsured.ca
                  </a>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#49628b]">
                    Adresse
                  </h3>
                  <a
                    href="https://maps.google.com/?q=Toronto+Ontario"
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm sm:text-base md:text-lg text-gray-800 hover:text-sky-500 transition leading-relaxed"
                  >
                    Toronto, Ontario
                    <br />
                    Région du Grand Toronto
                  </a>
                </div>

                <div className="pt-2 sm:pt-4">
                  <h4 className="text-lg sm:text-xl font-bold text-[#49628b] mb-4">
                    Zones desservies
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm sm:text-base md:text-lg text-gray-700">
                    {[
                      "Centre-ville de Toronto",
                      "North York",
                      "Scarborough",
                      "Etobicoke",
                      "East York",
                      "York",
                      "Liberty Village",
                      "The Beaches",
                      "Région du Grand Toronto (RGT)",
                    ].map((area, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 shrink-0" />
                        <span>{area}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-5">
                  <a
                    href="tel:18444400128"
                    className="inline-flex items-center justify-center rounded-full bg-[#00b4f0] px-6 sm:px-8 py-3 text-sm sm:text-base font-semibold text-white hover:bg-[#00a0d4] transition-colors w-full sm:w-auto"
                  >
                    Appeler maintenant
                  </a>

                  <a
                    href="https://maps.google.com/?q=Toronto+Ontario"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-full border-2 border-[#00b4f0] text-[#00b4f0] px-6 sm:px-8 py-3 text-sm sm:text-base font-semibold hover:bg-[#00b4f0] hover:text-white transition-colors w-full sm:w-auto"
                  >
                    Voir la zone desservie
                  </a>
                </div>
              </div>

              <div className="md:w-1/2">
                <div className="w-full h-64 sm:h-72 md:h-full min-h-[260px] lg:min-h-[320px] overflow-hidden rounded-2xl border border-gray-200">
                  <iframe
                    title="Zone desservie einsured.ca Toronto"
                    src="https://www.google.com/maps?q=Toronto,Ontario&z=11&output=embed"
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] leading-tight">
              Foire aux questions – Assurance vie à Toronto
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const expanded = activeIndex === index;
              const panelId = `faq-panel-${index}`;
              const btnId = `faq-btn-${index}`;

              return (
                <div
                  key={index}
                  className="border rounded-xl shadow-sm transition-all bg-white overflow-hidden"
                >
                  <button
                    id={btnId}
                    className="w-full flex justify-between items-center gap-4 text-left px-4 sm:px-6 py-4 font-semibold text-sm sm:text-base md:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                  >
                    <span className="pr-2">{faq.question}</span>
                    {expanded ? (
                      <Minus
                        className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 shrink-0"
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 shrink-0"
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {expanded && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={btnId}
                      className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base leading-relaxed"
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#f8fbff] text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
              Informations sur les licences et la réglementation
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-700">
              Informations importantes pour vous aider à comprendre nos normes de conseil et de conformité.
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {[
              "Conseillers en assurance vie agréés en Ontario",
              "Réglementé par l'ARSF Ontario",
              "Les produits sont soumis à l'approbation de souscription",
              "La disponibilité de la couverture dépend des directives de l'assureur",
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 flex items-start gap-3"
              >
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-sky-500 mt-0.5 shrink-0" />
                <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                  {item}
                </p>
              </div>
            ))}
          </div>

          <div className="max-w-5xl mx-auto mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3">
            <Link
              to="/fr/politique-confidentialite"
              className="text-sky-600 font-semibold hover:underline text-sm sm:text-base"
            >
              Politique de confidentialité
            </Link>
            <Link
              to="/fr/conditions-utilisation"
              className="text-sky-600 font-semibold hover:underline text-sm sm:text-base"
            >
              Conditions d'utilisation
            </Link>
          </div>
        </div>
      </section>

      {/* GET COVERED IN 3 EASY STEPS + CALENDAR */}
      <section ref={stepsRef} className="bg-white overflow-x-hidden">
        <style>{`
          @keyframes stepsPulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div
          className="bg-[#3b93c6] py-10 md:py-6"
          style={makeFadeScale(stepsInView, 0, 0.992, 780)}
        >
          <div className={`${siteContainer} px-4 sm:px-6 lg:px-10 text-center`}>
            <h2
              style={{
                ...makeFadeUp(stepsInView, 0, 16, 720),
                animation: stepsInView
                  ? `stepsPulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
              }}
              className="text-4xl sm:text-5xl md:text-5xl font-bold text-white drop-shadow"
            >
              Soyez couvert en 3 étapes faciles
            </h2>
          </div>
        </div>

        <div className={`${siteContainer} px-4 sm:px-6 lg:px-10 py-12 md:py-20 w-full max-w-full overflow-x-hidden`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-start lg:items-stretch">
            <div className="space-y-10 lg:space-y-10 lg:flex lg:flex-col lg:justify-center lg:h-full">
              <div className="space-y-7">
                <div
                  style={makeFadeLeft(stepsInView, 120, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 1}
                  onClick={() => toggleStepFlip(1)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(1);
                    }
                  }}
                >
                  <div className="relative min-h-[96px] sm:h-[84px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 1 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      <div className="absolute inset-0 flex items-center gap-0 [backface-visibility:hidden]">
                        <div className="w-[140px] sm:w-[160px] h-full flex items-center justify-center rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-gray-200">
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 1
                          </span>
                        </div>

                        <div className="flex-1 h-full flex items-center justify-center gap-3 bg-gradient-to-b from-[#4FA6D6] to-[#4B5F86] rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]">
                          <MessageCircle className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Contactez-nous
                          </span>
                        </div>
                      </div>

                      <div className="absolute inset-0 flex items-center [transform:rotateY(180deg)] [backface-visibility:hidden]">
                        <div className="w-[140px] sm:w-[160px] h-full flex items-center justify-center rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-gray-200">
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 1
                          </span>
                        </div>

                        <div className="flex-1 h-full flex items-center justify-center bg-white rounded-r-md border border-gray-200 shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6">
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            Contactez-nous ou réservez une rencontre — nous
                            vous guiderons personnellement
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={makeFadeLeft(stepsInView, 210, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 2}
                  onClick={() => toggleStepFlip(2)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(2);
                    }
                  }}
                >
                  <div className="relative min-h-[96px] sm:h-[84px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 2 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      <div className="absolute inset-0 flex items-center [backface-visibility:hidden]">
                        <div className="w-[140px] sm:w-[160px] h-full flex items-center justify-center rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-gray-200">
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 2
                          </span>
                        </div>

                        <div className="flex-1 h-full flex items-center justify-center gap-3 bg-gradient-to-b from-[#4FA6D6] to-[#4B5F86] rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]">
                          <BadgeCheck className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Choisissez votre régime
                          </span>
                        </div>
                      </div>

                      <div className="absolute inset-0 flex items-center [transform:rotateY(180deg)] [backface-visibility:hidden]">
                        <div className="w-[140px] sm:w-[160px] h-full flex items-center justify-center rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-gray-200">
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 2
                          </span>
                        </div>

                        <div className="flex-1 h-full flex items-center justify-center bg-white rounded-r-md border border-gray-200 shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6">
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            Nous vous présentons des solutions personnalisées
                            et vous choisissez celle qui vous convient le
                            mieux
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={makeFadeLeft(stepsInView, 300, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 3}
                  onClick={() => toggleStepFlip(3)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(3);
                    }
                  }}
                >
                  <div className="relative min-h-[96px] sm:h-[84px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 3 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      <div className="absolute inset-0 flex items-center [backface-visibility:hidden]">
                        <div className="w-[140px] sm:w-[160px] h-full flex items-center justify-center rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-gray-200">
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 3
                          </span>
                        </div>

                        <div className="flex-1 h-full flex items-center justify-center gap-3 bg-gradient-to-b from-[#4FA6D6] to-[#4B5F86] rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]">
                          <PlusSquare className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Obtenez votre approbation
                          </span>
                        </div>
                      </div>

                      <div className="absolute inset-0 flex items-center [transform:rotateY(180deg)] [backface-visibility:hidden]">
                        <div className="w-[140px] sm:w-[160px] h-full flex items-center justify-center rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-gray-200">
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 3
                          </span>
                        </div>

                        <div className="flex-1 h-full flex items-center justify-center bg-white rounded-r-md border border-gray-200 shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6">
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            Nous déposons votre demande et votre couverture
                            est activée rapidement
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <ThirtyMinuteTimerCard
                stepsInView={stepsInView}
                makeFadeScale={makeFadeScale}
              />
            </div>

            <div
  style={makeFadeRight(stepsInView, 180, 18, 760)}
  className="bg-white rounded-[32px] shadow-[0_18px_40px_rgba(0,0,0,0.18)] w-full max-w-full p-4 sm:p-5 overflow-hidden"
>
              <div className="rounded-[24px] overflow-hidden border border-gray-200 bg-white">
                <div className="relative h-[620px] sm:h-[720px] md:h-[760px] lg:h-[820px] overflow-y-auto">
                  <iframe
                    src="https://book.einsured.ca/widget/bookings/ei-team"
                    title="einsured.ca examen de 10 minutes"
                    loading="lazy"
                    className="w-full border-0 h-[980px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto bg-[#e8eff8] rounded-[24px] sm:rounded-[28px] p-5 sm:p-8 md:p-12 border border-[#d5e3f7] shadow-md">
            <div className="max-w-3xl mx-auto text-center mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
                Obtenir une soumission gratuite d'assurance vie à Toronto
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                Parlez à un conseiller agréé et explorez une couverture adaptée à votre famille, votre budget, vos besoins d'entreprise et vos objectifs à long terme.
              </p>
            </div>

            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 max-w-4xl mx-auto"
              noValidate
            >
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Nom complet"
                  maxLength={32}
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  aria-invalid={touched.name && !!errors.name}
                  className={
                    "w-full rounded-xl border px-4 py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-sky-400 " +
                    (touched.name && errors.name
                      ? "border-red-500"
                      : "border-gray-300")
                  }
                />
                {touched.name && errors.name ? (
                  <p className="mt-2 text-red-600 text-sm">{errors.name}</p>
                ) : null}
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Téléphone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={() => {
                    setTouched((t) => ({ ...t, phone: true }));
                    const digits = normalizeCanadianPhone(formData.phone);
                    if (digits) {
                      setFormData((prev) => ({
                        ...prev,
                        phone: formatPhoneForDisplay(digits),
                      }));
                    }
                  }}
                  aria-invalid={touched.phone && !!errors.phone}
                  className={
                    "w-full rounded-xl border px-4 py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-sky-400 " +
                    (touched.phone && errors.phone
                      ? "border-red-500"
                      : "border-gray-300")
                  }
                />
                {touched.phone && errors.phone ? (
                  <p className="mt-2 text-red-600 text-sm">{errors.phone}</p>
                ) : null}
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Courriel"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  aria-invalid={touched.email && !!errors.email}
                  className={
                    "w-full rounded-xl border px-4 py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-sky-400 " +
                    (touched.email && errors.email
                      ? "border-red-500"
                      : "border-gray-300")
                  }
                />
                {touched.email && errors.email ? (
                  <p className="mt-2 text-red-600 text-sm">{errors.email}</p>
                ) : null}
              </div>

              <div>
                <input
                  type="text"
                  name="coverageAmount"
                  placeholder="Montant de couverture"
                  value={formData.coverageAmount}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>

              <div className="md:col-span-2">
                <input
                  type="text"
                  name="preferredContactTime"
                  placeholder="Heure de contact préférée"
                  value={formData.preferredContactTime}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="md:col-span-2 bg-sky-500 text-white text-sm sm:text-base md:text-lg font-semibold px-6 sm:px-8 py-3 rounded-full hover:bg-sky-600 transition w-full md:w-auto md:mx-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "loading" ? "Envoi en cours..." : "Obtenir ma soumission gratuite"}
              </button>

              {submitted && status === "success" && (
                <p className="md:col-span-2 mt-2 border border-green-500 text-green-700 px-4 py-2 text-sm text-center rounded-lg bg-green-50">
                  Merci pour votre message. Il a bien été envoyé.
                </p>
              )}

              {status === "error" && (
                <p className="md:col-span-2 mt-2 border border-red-500 text-red-700 px-4 py-2 text-sm text-center rounded-lg bg-red-50">
                  {errorMsg}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LifeInsuranceToronto;
